import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Bot,
  CalendarDays,
  CalendarPlus,
  FilePlus2,
  FileText,
  Flag,
  IndianRupee,
  LayoutDashboard,
  Plus,
  Settings2,
  ShieldAlert,
  UserPlus,
  UserRound,
  Users
} from 'lucide-react';
import { matchPath } from 'react-router-dom';

import { hasPermission } from '@/features/auth/lib/permissions';
import { routePaths } from '@/routes/paths';

export type AppPageType = 'dashboard' | 'list' | 'detail' | 'form' | 'immersive-form';
export type AppChromeMode = 'standard' | 'immersive';
export type NavigationGroup = 'primary' | 'workspace' | 'manage' | 'admin';
type PermissionRequirement = string | string[] | undefined;

export type NavigationManifestItem = {
  key: string;
  path: string;
  matchPaths?: string[];
  title: string;
  icon: LucideIcon;
  group: NavigationGroup;
  description?: string;
  isPrimaryTab?: boolean;
  showInDrawer?: boolean;
  showInDesktopNav?: boolean;
  showInQuickActions?: boolean;
  permission?: PermissionRequirement;
  pageType: AppPageType;
  chromeMode: AppChromeMode;
  showBottomNav: boolean;
  showGlobalFab: boolean;
};

export type ShellQuickAction = {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  className: string;
  permission?: PermissionRequirement;
};

const navigationManifest: NavigationManifestItem[] = [
  {
    key: 'dashboard',
    path: routePaths.app.dashboard,
    title: 'Dashboard',
    icon: LayoutDashboard,
    group: 'primary',
    description: 'Clinic workspace',
    isPrimaryTab: true,
    showInDrawer: true,
    showInDesktopNav: true,
    pageType: 'dashboard',
    chromeMode: 'standard',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'patients',
    path: routePaths.app.patients,
    title: 'Patients',
    icon: Users,
    group: 'primary',
    description: 'Find records and continue care.',
    isPrimaryTab: true,
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: true
  },
  {
    key: 'patient-quick-add',
    path: routePaths.app.patientQuickAdd,
    title: 'Quick Register',
    icon: UserPlus,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'patient-new',
    path: routePaths.app.patientNew,
    title: 'Full Registration',
    icon: UserPlus,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'patient-detail',
    path: routePaths.app.patientDetails,
    title: 'Patient Profile',
    icon: Users,
    group: 'primary',
    pageType: 'detail',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'patient-history',
    path: routePaths.app.patientHistory,
    title: 'Patient History',
    icon: Users,
    group: 'primary',
    pageType: 'detail',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'patient-edit',
    path: routePaths.app.patientEdit,
    title: 'Edit Patient',
    icon: UserPlus,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'schedule',
    path: routePaths.app.schedule,
    title: 'Schedule',
    icon: CalendarDays,
    group: 'primary',
    description: 'Review today and upcoming visits.',
    isPrimaryTab: true,
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: true
  },
  {
    key: 'schedule-new',
    path: routePaths.app.scheduleNew,
    title: 'New Appointment',
    icon: CalendarPlus,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'invoices',
    path: routePaths.app.invoices,
    title: 'Bills',
    icon: IndianRupee,
    group: 'primary',
    description: 'Track billing and collections.',
    isPrimaryTab: true,
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: true
  },
  {
    key: 'invoice-new',
    path: routePaths.app.invoiceNew,
    title: 'New Bill',
    icon: FilePlus2,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'invoice-edit',
    path: routePaths.app.invoiceEdit,
    title: 'Edit Bill',
    icon: FilePlus2,
    group: 'primary',
    pageType: 'immersive-form',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'reports',
    path: routePaths.app.reports,
    title: 'Reports',
    icon: FileText,
    group: 'workspace',
    description: 'Check clinic performance quickly.',
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'assistant',
    path: routePaths.app.assistant,
    title: 'Assistant',
    icon: Bot,
    group: 'workspace',
    description: 'Ask for guided help and actions.',
    showInDrawer: true,
    showInDesktopNav: true,
    pageType: 'detail',
    chromeMode: 'immersive',
    showBottomNav: false,
    showGlobalFab: false
  },
  {
    key: 'profile',
    path: routePaths.app.profile,
    title: 'Profile',
    icon: UserRound,
    group: 'workspace',
    description: 'Update your account and clinic details.',
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    pageType: 'detail',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'getting-started',
    path: routePaths.app.gettingStarted,
    title: 'Getting Started',
    icon: Flag,
    group: 'workspace',
    description: 'Set up clinic workflows and basics.',
    showInDrawer: true,
    showInDesktopNav: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'masters-menu',
    path: routePaths.app.mastersMenu,
    title: 'Master / Setup',
    icon: Settings2,
    group: 'manage',
    description: 'Doctors, procedures, and appointment types.',
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    permission: ['setup.access', 'masters.read'],
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'masters',
    path: routePaths.app.masters,
    title: 'Notification Settings',
    icon: Settings2,
    group: 'manage',
    permission: ['setup.access', 'masters.read'],
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'masters-doctors',
    path: routePaths.app.mastersDoctors,
    title: 'Doctors',
    icon: Settings2,
    group: 'manage',
    permission: ['setup.access', 'masters.read'],
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'masters-procedures',
    path: routePaths.app.mastersProcedures,
    title: 'Procedures',
    icon: Settings2,
    group: 'manage',
    permission: ['setup.access', 'masters.read'],
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'masters-appointment-types',
    path: routePaths.app.mastersAppointmentTypes,
    title: 'Appointment Types',
    icon: Settings2,
    group: 'manage',
    permission: ['setup.access', 'masters.read'],
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-menu',
    path: routePaths.app.adminMenu,
    title: 'Admin',
    icon: Settings2,
    group: 'admin',
    description: 'Users, roles, profile, and diagnostics.',
    showInDrawer: true,
    showInDesktopNav: true,
    showInQuickActions: true,
    permission: 'users.manage',
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-clinic-profile',
    path: routePaths.app.adminClinicProfile,
    title: 'Clinic Profile',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'form',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-import',
    path: routePaths.app.adminImport,
    title: 'Import Data',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-roles',
    path: routePaths.app.adminRoles,
    title: 'Roles',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-role-new',
    path: routePaths.app.adminRoleNew,
    title: 'Role Permissions',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'form',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-role-details',
    path: routePaths.app.adminRoleDetails,
    title: 'Role Permissions',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'form',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-notification-deliveries',
    path: routePaths.app.adminNotificationDeliveries,
    title: 'Notification Status',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'admin-users',
    path: routePaths.app.adminUsers,
    title: 'Users',
    icon: Settings2,
    group: 'admin',
    permission: 'users.manage',
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'owner',
    path: routePaths.app.owner,
    title: 'Clinic Owner',
    icon: UserRound,
    group: 'workspace',
    pageType: 'detail',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'errors',
    path: routePaths.app.errors,
    title: 'Errors',
    icon: ShieldAlert,
    group: 'admin',
    permission: 'users.manage',
    showInDrawer: true,
    showInDesktopNav: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  },
  {
    key: 'system-health',
    path: routePaths.app.systemHealth,
    title: 'System Health',
    icon: Activity,
    group: 'admin',
    permission: 'users.manage',
    showInDrawer: true,
    showInDesktopNav: true,
    pageType: 'list',
    chromeMode: 'standard',
    showBottomNav: true,
    showGlobalFab: false
  }
];

const globalQuickActions: ShellQuickAction[] = [
  {
    key: 'patient-quick-add',
    label: 'Quick Add',
    to: routePaths.app.patientQuickAdd,
    icon: Plus,
    className: 'bg-amber-500 hover:bg-amber-600'
  },
  {
    key: 'patient-new',
    label: 'Full Registration',
    to: routePaths.app.patientNew,
    icon: UserPlus,
    className: 'bg-slate-700 hover:bg-slate-800'
  },
  {
    key: 'invoice-new',
    label: 'New Bill',
    to: routePaths.app.invoiceNew,
    icon: FilePlus2,
    className: 'bg-emerald-600 hover:bg-emerald-700'
  },
  {
    key: 'schedule-new',
    label: 'New Appointment',
    to: routePaths.app.scheduleNew,
    icon: CalendarPlus,
    className: 'bg-brand-600 hover:bg-brand-700'
  }
];

const navSectionOrder: Array<{ key: NavigationGroup; label: string }> = [
  { key: 'primary', label: 'Core' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'manage', label: 'Manage' },
  { key: 'admin', label: 'Admin' }
];

function canAccess(permissions: string[] | undefined, permission?: PermissionRequirement) {
  if (!permission) {
    return true;
  }

  return hasPermission(permissions, permission);
}

function matchesPathname(item: NavigationManifestItem, pathname: string) {
  const patterns = item.matchPaths ?? [item.path];
  return patterns.some(pattern => Boolean(matchPath({ path: pattern, end: true }, pathname)));
}

export function resolveNavigationItem(pathname: string) {
  return (
    navigationManifest.find(item => matchesPathname(item, pathname)) ?? {
      key: 'fallback',
      path: pathname,
      title: 'AarogyaFlow',
      icon: LayoutDashboard,
      group: 'workspace' as const,
      pageType: 'list' as const,
      chromeMode: 'standard' as const,
      showBottomNav: true,
      showGlobalFab: false
    }
  );
}

export function getPrimaryTabItems(permissions: string[] | undefined) {
  return navigationManifest.filter(item => item.isPrimaryTab && canAccess(permissions, item.permission));
}

export function getDrawerSections(permissions: string[] | undefined) {
  return navSectionOrder
    .map(section => ({
      ...section,
      items: navigationManifest.filter(
        item =>
          item.group === section.key &&
          item.showInDrawer &&
          !item.isPrimaryTab &&
          canAccess(permissions, item.permission)
      )
    }))
    .filter(section => section.items.length > 0);
}

export function getDesktopNavSections(permissions: string[] | undefined) {
  return navSectionOrder
    .map(section => ({
      ...section,
      items: navigationManifest.filter(
        item => item.group === section.key && item.showInDesktopNav && canAccess(permissions, item.permission)
      )
    }))
    .filter(section => section.items.length > 0);
}

export function getDashboardQuickActions(permissions: string[] | undefined) {
  return navigationManifest.filter(item => item.showInQuickActions && canAccess(permissions, item.permission));
}

export function getGlobalQuickActions(permissions: string[] | undefined) {
  return globalQuickActions.filter(item => canAccess(permissions, item.permission));
}
